/**
 * SanPlumb — Constantes de presentación (Fase 3)
 *
 * Datos de referencia para la UI: opciones de routing y listas de marcas
 * del catálogo. Las opciones de routing reflejan las 3 estrategias del
 * engine (`engine/three/sceneBuilder.ts`).
 *
 * NOTA v1.4: la separación AF/AC documentada aquí es 3 cm (IS.010 §2.3.i),
 * corregida respecto al texto del diseño original que decía 15 cm.
 */

import type { RoutingOption, BrandState } from '../types/ui';
import type { RoutingStrategy } from '../types';

/**
 * Las 3 estrategias de routing en orden de ciclo (Regenerar 3D rota A→B→C→A).
 * El default normativo es 'muro_dual' (§XV).
 */
export const ROUTING_OPTIONS: readonly RoutingOption[] = [
  {
    id: 'muro_dual',
    label: 'A · Muro Dual',
    desc: 'AF + AC en el mismo muro a +0.30m NPT, separadas ≥3cm (IS.010 §2.3.i, rec. 5–7cm)',
  },
  {
    id: 'mixta',
    label: 'B · Mixta',
    desc: 'AF empotrada en muro (+0.30m) · AC corre por contrapiso (+0.05m)',
  },
  {
    id: 'piso_dual',
    label: 'C · Piso Dual',
    desc: 'AF + AC ambas por contrapiso, suben en cada aparato',
  },
] as const;

/** Estrategia de routing inicial — default normativo §XV. */
export const DEFAULT_ROUTING: RoutingStrategy = 'muro_dual';

/** Devuelve la siguiente estrategia en el ciclo A→B→C→A. */
export function nextRoutingId(current: RoutingStrategy): RoutingStrategy {
  const i = ROUTING_OPTIONS.findIndex((o) => o.id === current);
  return ROUTING_OPTIONS[(i + 1) % ROUTING_OPTIONS.length].id;
}

/** Devuelve la opción completa dado un id. */
export function routingOption(id: RoutingStrategy): RoutingOption {
  return ROUTING_OPTIONS.find((o) => o.id === id) ?? ROUTING_OPTIONS[0];
}

/**
 * Marcas disponibles por categoría de material (mercado peruano).
 * El usuario elige una por categoría en el panel Catálogo.
 */
export const BRANDS: Readonly<Record<keyof BrandState, readonly string[]>> = {
  pipeAF: ['PAVCO', 'Tigre'],
  pipeAC: ['Vinilit', 'Tigre'],
  valveAF: ['Italgrif', 'Vainsa'],
  valveAC: ['Vinilit', 'Italgrif'],
  fixtures: ['Trebol', 'Vainsa'],
} as const;

/** Selección de marcas por defecto. */
export const DEFAULT_BRANDS: BrandState = {
  pipeAF: 'PAVCO',
  pipeAC: 'Vinilit',
  valveAF: 'Italgrif',
  valveAC: 'Vinilit',
  fixtures: 'Trebol',
};
